import Vue from "vue";
import Vuex from "vuex";
import { getField, updateField } from "vuex-map-fields";
Vue.use(Vuex);

// -----------------------------------------------------

const state = {
  debugMode: false,
  loadingComplete: false,
  jsonLoaded: false,

  employeeName: null,
  reviewDate: null,
  reviewType: null,
  memberNumber: null,
  reviewSalary: null,
  superScheme: null,
  birthDate: null,

  reductionData: [
    {
      reductionDate: null,
      baseSalaryPrior: null,
      hdaPrior: null,
      allowancePrior: null,
      pipPayable: null,
      salReviewCompleted: null,
      curEquivAvailable: null
    }
  ],

  Enum: {
    superScheme: {
      CSS: "CSS",
      PSS: "PSS",
      PSSAP: "PSSAP"
    },
    reviewType: {
      BIRTHDAY: "BIRTHDAY",
      EXIT: "EXIT"
    },
    btnVal: {
      YES: "YES",
      NO: "NO"
    },
    january2003: 20030101,
    july2003: 20030701
  },

  formatOptions: {
    date: {
      date: true,
      datePattern: ["d", "m", "Y"],
      delimiter: "/"
    },
    salary: {
      numeral: true,
      numeralPositiveOnly: true,
      noImmediatePrefix: true,
      rawValueTrimPrefix: true,
      numeralIntegerScale: 9,
      numeralDecimalScale: 2
    },
    number: {
      numericOnly: true,
      blocks: [3, 3, 3],
      delimiter: "-"
    }
  },

  awoteData: {
    datesAndRates: null,
    lastUpdated: null,
    nextExpected: null,
    nextExpectedPubDate: null
  }
};

// -----------------------------------------------------

const mutations = {
  resetEmployeeFields(state) {
    state.employeeName = null;
    state.reviewDate = null;
    state.reviewType = null;
    state.memberNumber = null;
    state.reviewSalary = null;
    state.superScheme = null;
    state.birthDate = null;
  },

  resetReductions(state) {
    state.reductionData = [
      {
        reductionDate: null,
        baseSalaryPrior: null,
        hdaPrior: null,
        allowancePrior: null,
        pipPayable: null,
        salReviewCompleted: null,
        curEquivAvailable: null
      }
    ];
  },

  updateReviewType(state, value) {
    state.reviewType = value;
  },

  updateSuperScheme(state, value) {
    state.superScheme = value;
  },

  updateEmployeeName(state, value) {
    state.employeeName = value;
  },

  nullButtonFields(state, value) {
    var index = 0;
    if (value !== state.Enum.superScheme.CSS) {
      for (index = 0; index < state.reductionData.length; index++) {
        state.reductionData[index].salReviewCompleted = null;
      }
    }
    if (
      value !== state.Enum.superScheme.PSS &&
      value !== state.Enum.superScheme.PSSAP
    ) {
      for (index = 0; index < state.reductionData.length; index++) {
        state.reductionData[index].pipPayable = null;
      }
    }
  },

  addReduction(state) {
    state.reductionData.push({
      // Empty item to clone
      reductionDate: null,
      baseSalaryPrior: null,
      hdaPrior: null,
      allowancePrior: null,
      pipPayable: null,
      salReviewCompleted: null,
      curEquivAvailable: null
    });
  },

  deleteReduction(state, index) {
    state.reductionData.splice(index, 1);
  },

  sortReductions(state) {
    state.reductionData.sort(sortReductionData);
  },

  updatePipPayable(state, data) {
    state.reductionData[data.index].pipPayable = data.value;
  },

  updateSalReviewCompleted(state, data) {
    state.reductionData[data.index].salReviewCompleted = data.value;
  },

  updateCurEquivAvailable(state, data) {
    state.reductionData[data.index].curEquivAvailable = data.value;
  },

  readJSONData(state, data) {
    state.awoteData.datesAndRates = data.datesAndRates;
    state.awoteData.lastUpdated = data.lastUpdated;
    state.awoteData.nextExpected = data.nextExpected;
    state.awoteData.nextExpectedPubDate = data.nextExpectedPubDate;
    state.jsonLoaded = true;
    state.loadingComplete = true;
  },

  jsonError(state) {
    state.loadingComplete = true;
  },

  updateField
};

const actions = {
  readJSONData({ commit }) {
    return new Promise((resolve, reject) => {
      let jsonDataURL = "data/rates.json" + "?cb=" + new Date().getTime();
      let xhr = new XMLHttpRequest();

      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status <= 300) {
          try {
            let data = JSON.parse(xhr.responseText);
            commit("readJSONData", data);
          } catch (err) {
            commit("jsonError");
          }
          resolve();
        } else {
          commit("jsonError");
          reject();
        }
      };

      xhr.open("GET", jsonDataURL, true);
      xhr.send();
    });
  }
};

// -----------------------------------------------------

const getters = {
  debugMode: state => {
    return state.debugMode;
  },
  loadingComplete: state => {
    return state.loadingComplete;
  },
  jsonLoaded: state => {
    return state.jsonLoaded;
  },
  awoteData: state => {
    return state.awoteData.datesAndRates;
  },
  awoteLastPubDate: state => {
    return state.awoteData.datesAndRates[
      state.awoteData.datesAndRates.length - 1
    ].pubDate;
  },
  awoteNextExpectedPubDate: state => {
    return state.awoteData.nextExpectedPubDate;
  },
  awoteLastUpdatedUpdateText: state => {
    return state.awoteData.lastUpdated;
  },
  awoteNextExpectedUpdateText: state => {
    return state.awoteData.nextExpected;
  },
  employeeName: state => {
    return state.employeeName;
  },
  memberNumber: state => {
    return state.memberNumber;
  },
  birthDate: state => {
    return state.birthDate;
  },
  reviewType: state => {
    return state.reviewType;
  },
  reviewSalary: state => {
    return state.reviewSalary;
  },
  reviewDate: state => {
    return state.reviewDate;
  },
  superScheme: state => {
    return state.superScheme;
  },
  reductionCount: state => {
    return state.reductionData !== null && state.reductionData !== undefined
      ? state.reductionData.length
      : 0;
  },
  reductionData: state => {
    return state.reductionData;
  },
  formatOptions: state => {
    return state.formatOptions;
  },
  Enum: state => {
    return state.Enum;
  },
  getField
};

// -----------------------------------------------------

function dateToNum(dateVal) {
  return parseInt(
    dateVal
      .split("/")
      .reverse()
      .join("")
  );
}

function sortReductionData(x, y) {
  if (dateToNum(x.reductionDate) === dateToNum(y.reductionDate)) {
    return 0;
  } else {
    return dateToNum(x.reductionDate) < dateToNum(y.reductionDate) ? -1 : 1;
  }
}

// -----------------------------------------------------

const store = new Vuex.Store({
  state,
  mutations,
  actions,
  getters
});

store.dispatch("readJSONData");

export default store;
